import { defineStore, storeToRefs } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useNow, useStorage } from '@vueuse/core';
import { useAuthStore } from './authStore';
import heroImage from '@/assets/spielpause.png';
import { afterAll } from 'src/pages/heroDescription';

import {
  loadSections,
  saveSections,
  updateSectionOrder,
  listImages,
  uploadImage,
  type ImagePoolItem,
} from 'src/services/userService';
import type {
  ImageState,
  ImgTextState,
} from 'src/components/image-text-ensemble/types';

interface SectionObject {
  id: string;
  title?: string;
  subtitle?: string;
  img?: string | null;
  html: string;
  ctaLabel?: string;
  ctaHref?: string;
  sortOrder?: number;
  sectionKeyRaw?: string;
  recordId?: number;
  imageState?: ImageState;
  layout?: {
    imageSplitPercent?: number;
  };
  imgText?: ImgTextState;
}

const editorRoles = ['developer', 'admin', 'manager'];

export const appStore = defineStore('appStore', () => {
  const authStore = useAuthStore();
  const { useAuthorizedFetch } = authStore;
  const { isAuthenticated, user } = storeToRefs(authStore);
  const sectionData = ref<SectionObject[]>([]);
  const imagePool = ref<ImagePoolItem[]>([]);
  const imagePoolError = ref<string | null>(null);
  const originalSectionSnapshot = ref<SectionObject[]>([]);
  const initialized = ref(false);
  const sectionLoadError = ref<string | null>(null);
  const previewMode = ref(true);
  const darkMode = useStorage<boolean>('tt-dark-mode', true);
  let initializePromise: Promise<boolean> | null = null;
  const homeHeroImageSrc = ref(heroImage);
  const phaseNow = useNow({ interval: 60_000 });
  const isSpielpause = computed(() => afterAll(phaseNow.value.getTime()));

  const canEditContent = computed(() => {
    if (authStore.DEV_AUTH_MOCK_ENABLED) {
      return true;
    }

    if (!isAuthenticated.value) {
      return false;
    }

    const roles = user.value?.roles ?? [];
    return roles.some((role: string) => editorRoles.includes(role));
  });

  watch(
    canEditContent,
    (allowed) => {
      if (!allowed) {
        previewMode.value = true;
      }
    },
    { immediate: true },
  );

  const setPreviewMode = (next: boolean) => {
    previewMode.value = Boolean(next);
  };

  const togglePreviewMode = () => {
    previewMode.value = !previewMode.value;
  };

  const setDarkMode = (next: boolean) => {
    darkMode.value = Boolean(next);
  };

  const toggleDarkMode = () => {
    darkMode.value = !darkMode.value;
  };

  // initialize()

  const edited = computed(() =>
    sectionData.value
      .filter((sd) => sd.id === 'actual')
      .map((item) => item.html),
  );

  const hasSectionChanges = computed(() => {
    return (
      JSON.stringify(sectionData.value) !==
      JSON.stringify(originalSectionSnapshot.value)
    );
  });

  const loadImagePool = async () => {
    imagePoolError.value = null;
    try {
      const items = await listImages();
      imagePool.value = Array.isArray(items) ? items : [];
    } catch (error) {
      console.error('[AppStore] Failed to load image pool', error);
      imagePool.value = [];
      imagePoolError.value =
        error instanceof Error
          ? error.message
          : 'Bild-Pool konnte nicht geladen werden.';
    }
    return imagePool.value;
  };

  const restoreSectionSnapshot = () => {
    sectionData.value = JSON.parse(
      JSON.stringify(originalSectionSnapshot.value),
    ) as SectionObject[];
  };
  const loadLocalSectionData = async () => {
    console.info('[AppStore] loadLocalSectionData called');
    sectionLoadError.value = null;
    try {
      const payload = await loadSections();
      sectionData.value = Array.isArray(payload) ? payload : [];
      originalSectionSnapshot.value = JSON.parse(
        JSON.stringify(sectionData.value),
      ) as SectionObject[];
      initialized.value = true;
      console.info('[AppStore] local section data loaded', {
        entries: sectionData.value.length,
      });
      return true;
    } catch (fallbackError) {
      console.error(
        'Failed to load local section data fallback',
        fallbackError,
      );
      sectionLoadError.value =
        fallbackError instanceof Error
          ? fallbackError.message
          : 'Inhalte konnten nicht geladen werden.';
      return false;
    }
  };

  async function initialize() {
    if (initialized.value) {
      console.info('[AppStore] initialize skipped (already initialized)');
      return true;
    }

    if (initializePromise) {
      console.info('[AppStore] initialize joined existing promise');
      return initializePromise;
    }

    const run = async () => {
      try {
        console.info('[AppStore] initialize start (local data)');
        const loaded = await loadLocalSectionData();
        if (!loaded) {
          console.warn('[AppStore] Failed to load local section data');
        }
        return loaded;
      } finally {
        console.info('[AppStore] initialize run complete', {
          initialized: initialized.value,
        });
      }
    };

    initializePromise = run()
      .catch((error) => {
        console.error('[AppStore] initialize encountered error', error);
        return false;
      })
      .finally(() => {
        initializePromise = null;
      });

    return initializePromise;

    //preload runtime images from server
    // try {
    //   await Promise.all(sectionData.value.filter((sd => sd.img)).map(sd => loadImage(`${baseUrl}/public/images/${sd.img}`)));
    //   console.log('All images have been loaded successfully!');
    //   // Perform actions after all images are loaded
    //   initialized.value = true
    //   console.log('initialized', initialized.value)
    // } catch (error) {
    //   console.error(error);
    // }
  }

  async function uploadFileToServer(filename: string, htmlContent: string) {
    const { data, error } = await useAuthorizedFetch('/write-file')
      .post({ filename, content: htmlContent })
      .json();

    if (error.value) {
      console.error('Error:', error.value);
      throw error.value;
    }

    return data.value!;
  }

  async function saveSectionData() {
    // Detect deleted sections by comparing current recordIds against snapshot
    const currentRecordIds = new Set(
      sectionData.value
        .map((s) => s.recordId)
        .filter((id): id is number => typeof id === 'number'),
    );
    const deleteIds = originalSectionSnapshot.value
      .map((s) => s.recordId)
      .filter(
        (id): id is number =>
          typeof id === 'number' && !currentRecordIds.has(id),
      );

    const hasContentChanges =
      JSON.stringify(sectionData.value) !==
      JSON.stringify(originalSectionSnapshot.value);

    // Nothing changed and nothing deleted — skip
    if (!hasContentChanges && deleteIds.length === 0) {
      return true;
    }
    const returnedItems = await saveSections(
      sectionData.value,
      deleteIds.length > 0 ? deleteIds : undefined,
    );

    // Assign server-generated IDs to newly created sections.
    if (returnedItems?.length) {
      for (let ri = 0; ri < returnedItems.length; ri++) {
        const serverItem = returnedItems[ri];
        const section = sectionData.value[ri];
        if (
          section &&
          serverItem &&
          typeof serverItem.id === 'number' &&
          section.recordId == null
        ) {
          section.recordId = serverItem.id;
        }
      }
    }

    // Persist order per section_key so all rows in a section get a consistent sort_order.
    const orderBySectionKey = new Map<
      string,
      Array<{ id: number; sortOrder: number }>
    >();
    const nextOrderBySectionKey = new Map<string, number>();
    sectionData.value.forEach((section) => {
      if (typeof section.recordId !== 'number') {
        return;
      }

      const sectionKey =
        String(section.sectionKeyRaw ?? section.id ?? 'actual').trim() ||
        'actual';
      const nextSortOrder = (nextOrderBySectionKey.get(sectionKey) ?? 0) + 1;
      nextOrderBySectionKey.set(sectionKey, nextSortOrder);

      const entries = orderBySectionKey.get(sectionKey) ?? [];
      entries.push({
        id: section.recordId,
        sortOrder: nextSortOrder,
      });
      orderBySectionKey.set(sectionKey, entries);
    });

    for (const [sectionKey, items] of orderBySectionKey.entries()) {
      if (!items.length) {
        continue;
      }
      await updateSectionOrder(sectionKey, items);
    }

    originalSectionSnapshot.value = JSON.parse(
      JSON.stringify(sectionData.value),
    ) as SectionObject[];
    return true;
  }

  async function uploadImageToServer(imageFile: File, tags?: string[]) {
    return uploadImage(imageFile, undefined, tags);
  }

  async function retrieveFileFromServer(filename: string) {
    console.log(
      'TCL - file: appStore.ts:85 - retrieveFileFromServer - retrieveFileFromServer:',
      filename,
    );

    const { data, error } = await useAuthorizedFetch(
      `/read-file?filename=${encodeURIComponent(filename)}`,
    )
      .get()
      .json();

    if (error.value) {
      throw new Error(`Error: ${error.value.message}`);
    }

    if (!data.value) {
      throw new Error('Invalid response from the server');
    }

    return data.value;
  }

  return {
    sectionData,
    imagePool,
    imagePoolError,
    sectionLoadError,
    originalSectionSnapshot,
    previewMode,
    darkMode,
    homeHeroImageSrc,
    isSpielpause,
    canEditContent,
    initialize,
    initialized,
    edited,
    hasSectionChanges,
    uploadFileToServer,
    uploadImageToServer,
    retrieveFileFromServer,
    saveSectionData,
    loadImagePool,
    restoreSectionSnapshot,
    setPreviewMode,
    togglePreviewMode,
    setDarkMode,
    toggleDarkMode,
  };
});
